import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDateTime, formatNumber, summarizeUserAgent } from '../../utils/formatUtils';
import { PageHeader, ClientChip, auditLabel } from '../../components/common';

const useDebounced = (value, ms = 400) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const LoginHistory = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search.trim());
  const [filters, setFilters] = useState({ success: '', action: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState({ items: [], total: 0 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/login-history', {
        params: {
          search: debounced,
          success: filters.success,
          action: filters.action,
          from: filters.from ? new Date(`${filters.from}T00:00:00+09:00`).toISOString() : '',
          to: filters.to ? new Date(`${filters.to}T23:59:59+09:00`).toISOString() : '',
          page,
          pageSize,
        },
      });
      setData(res.data);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [debounced, filters, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => {
    setFilters((f) => ({ ...f, [key]: e.target.value }));
    setPage(1);
  };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder="이메일 · 이름 · IP · 회원ID"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select size="small" label="결과" value={filters.success} onChange={setFilter('success')} sx={{ minWidth: 110 }}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="true">성공</MenuItem>
            <MenuItem value="false">실패</MenuItem>
          </TextField>
          <TextField select size="small" label="이벤트" value={filters.action} onChange={setFilter('action')} sx={{ minWidth: 120 }}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="LOGIN">로그인</MenuItem>
            <MenuItem value="LOGOUT">로그아웃</MenuItem>
            <MenuItem value="REGISTER">회원가입</MenuItem>
            <MenuItem value="PW_RESET_REQUEST">비밀번호 찾기 요청</MenuItem>
            <MenuItem value="PW_RESET">비밀번호 재설정</MenuItem>
          </TextField>
          <TextField type="date" size="small" label="시작일" value={filters.from} onChange={setFilter('from')} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label="종료일" value={filters.to} onChange={setFilter('to')} InputLabelProps={{ shrink: true }} />
        </Stack>
      </Paper>
      <Paper>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>시각</TableCell>
              <TableCell>회원</TableCell>
              <TableCell>이벤트</TableCell>
              <TableCell>구분</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>기기</TableCell>
              <TableCell>결과</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>기록이 없습니다.</TableCell>
              </TableRow>
            )}
            {data.items.map((h) => (
              <TableRow
                key={h.id}
                hover
                sx={{ cursor: h.userId ? 'pointer' : 'default' }}
                onClick={() => h.userId && navigate(`/admin/users/${encodeURIComponent(h.userId)}`)}
              >
                <TableCell>{formatKstDateTime(h.createdAt)}</TableCell>
                <TableCell>
                  <Typography variant="body2">{h.username || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{h.email}{h.userId ? ` · #${h.userId}` : ''}</Typography>
                </TableCell>
                <TableCell>{h.action}</TableCell>
                <TableCell><ClientChip client={h.client} /></TableCell>
                <TableCell>{h.ipAddress}</TableCell>
                <TableCell>
                  <Tooltip title={h.userAgent || ''}><span>{summarizeUserAgent(h.userAgent)}</span></Tooltip>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={h.success ? '성공' : h.message || '실패'} color={h.success ? 'success' : 'error'} variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.total}
          page={page - 1}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[50, 100, 200, 500]}
          onPageChange={(_e, p) => setPage(p + 1)}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          labelRowsPerPage="페이지당"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${formatNumber(count)}`}
        />
      </Paper>
    </>
  );
};

const AuditLogs = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient
      .get('/admin/audit-logs', { params: { page, pageSize: 50 } })
      .then((res) => {
        setData(res.data);
        setError('');
      })
      .catch((e) => setError(getErrorMessage(e)));
  }, [page]);

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>시각</TableCell>
              <TableCell>관리자</TableCell>
              <TableCell>작업</TableCell>
              <TableCell>대상 회원</TableCell>
              <TableCell>내용</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>기록이 없습니다.</TableCell>
              </TableRow>
            )}
            {data.items.map((l) => (
              <TableRow
                key={l.id}
                hover
                sx={{ cursor: l.targetUserId ? 'pointer' : 'default' }}
                onClick={() => l.targetUserId && navigate(`/admin/users/${encodeURIComponent(l.targetUserId)}`)}
              >
                <TableCell>{formatKstDateTime(l.createdAt)}</TableCell>
                <TableCell>{l.adminUsername || l.adminUserId}</TableCell>
                <TableCell>{auditLabel(l.action)}</TableCell>
                <TableCell>{l.targetUserId ? `#${l.targetUserId}` : '-'}</TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {l.detail ? JSON.stringify(l.detail) : '-'}
                  </Typography>
                </TableCell>
                <TableCell>{l.ipAddress}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.total}
          page={page - 1}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          onPageChange={(_e, p) => setPage(p + 1)}
        />
      </Paper>
    </>
  );
};

const History = () => {
  const [tab, setTab] = useState(0);
  return (
    <>
      <PageHeader title="접속·작업 이력" description="로그인·로그아웃·가입 기록과 관리자가 한 작업 기록" />
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="로그인 이력" />
        <Tab label="관리자 작업 기록" />
      </Tabs>
      {tab === 0 ? <LoginHistory /> : <AuditLogs />}
    </>
  );
};

export default History;
